import { api, apiBlob, apiUpload } from '../../../lib/api';
import type {
  CreateRepositoryFolderRequest,
  RepositoryBrowseResult,

  RepositoryFolderOption,
  RepositoryDocumentVersionListItem,
  RepositoryItemListItem,
  RepositoryQuery,
  RepositoryStorage,
  RepositoryUploadDefaults,
  UpdateRepositoryFolderRequest,
  SaveRepositorySharesRequest,
  RepositoryShareSummary,
  SetRepositoryGeneralAccessRequest,
} from './repositoryTypes';

function toQueryString(query: RepositoryQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check here would drop it and
    the server would read the axis as unmentioned, which shows everything instead of nothing.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.type !== undefined) params.set('type', query.type);

  // Null IS the root, and it has to be distinguishable from "not scoped at all" — so the
  // parameter is omitted for the root rather than sent empty.
  if (query.folderId) params.set('folderId', query.folderId);

  if (query.allFolders) params.set('allFolders', 'true');
  if (query.pinnedOnly) params.set('pinnedOnly', 'true');
  if (query.expiredOnly) params.set('expiredOnly', 'true');
  if (query.mineOnly) params.set('mineOnly', 'true');
  if (query.sharedOnly) params.set('sharedOnly', 'true');

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const repositoryApi = {
  /** One folder's contents, paged, with the breadcrumb that says where the reader is. */
  browse: (query: RepositoryQuery, signal?: AbortSignal) =>
    api<RepositoryBrowseResult>(`/api/repository${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<RepositoryItemListItem>(`/api/repository/${id}`, { signal }),

  /**
   * Active folders for the parent picker. Unpaged — a picker that silently stopped at a page
   * boundary would hide choices without saying so.
   */
  folderOptions: (excludeSubtreeOf?: string, signal?: AbortSignal) =>
    api<RepositoryFolderOption[]>(
      `/api/repository/folder-options${excludeSubtreeOf ? `?excludeSubtreeOf=${excludeSubtreeOf}` : ''}`,
      { signal },
    ),

  /**
   * Uploads ONE file into a folder, creating the item and storing the bytes in one call.
   *
   * Multipart, matching the Files module's own upload — one upload shape in the product.
   *
   * **One file per call.** The queue decides whether a batch goes all at once or one after
   * another; either way each row is its own request, which is what gives a per-file status, a
   * per-file progress bar, and a per-file retry.
   *
   * The FOLDER decides what gets created — inside a coded branch a controlled document with a
   * permanent reference number and a required classification, outside one a plain file.
   */
  upload: (
    file: File,
    request: {
      folderId: string;
      name: string;
      classificationId?: string | null;
      description?: string | null;
    },
    options: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {},
  ) => {
    const form = new FormData();

    form.append('file', file);
    form.append('FolderId', request.folderId);
    form.append('Name', request.name);

    /*
      No department. It is the uploader's own, resolved server-side from their record with SYS
      as the floor — product call, 18 Sep 2026 — and the request has no field for it at all,
      which is what makes the code unspoofable rather than merely unspoofed.
    */

    // Omitted rather than sent empty: an empty string would bind to a Guid the server then
    // has to reject.
    if (request.classificationId) form.append('ClassificationId', request.classificationId);
    if (request.description) form.append('Description', request.description);

    // apiUpload, not api: the queue shows a per-file progress bar, and fetch cannot report
    // upload progress. See its doc comment — it is not the default for anything else.
    return apiUpload<RepositoryItemListItem>('/api/repository/files', form, options);
  },

  /**
   * The business unit this caller's uploads are filed under — their own, resolved server-side
   * from their record in one call rather than five requests, with SYS as the floor.
   *
   * Read to DISPLAY only — the queue states it once in its footer. Nothing is sent back.
   */
  uploadDefaults: (signal?: AbortSignal) =>
    api<RepositoryUploadDefaults>('/api/repository/upload-defaults', { signal }),

  /**
   * Supersedes a document's file and advances its version — the DIM's revision path.
   *
   * Multipart, and the file is **optional**: without one the document keeps its current bytes
   * and only the version advances. The current file is archived into the history either way.
   */
  replace: (
    id: string,
    request: { increment: number; changeDescription?: string | null; file?: File | null },
  ) => {
    const form = new FormData();

    form.append('Increment', String(request.increment));

    if (request.changeDescription) form.append('ChangeDescription', request.changeDescription);

    // Appended only when there IS one. An empty part is what a browser sends for an untouched
    // file input, and the server reads that as absent — but not sending it at all is clearer.
    if (request.file) form.append('file', request.file);

    return api<RepositoryItemListItem>(`/api/repository/${id}/versions`, {
      method: 'POST',
      body: form,
    });
  },

  /** A document's superseded files, newest version first. */
  versions: (id: string, signal?: AbortSignal) =>
    api<RepositoryDocumentVersionListItem[]>(`/api/repository/${id}/versions`, { signal }),

  /**
   * ONE archived version's file — the bytes the document carried at that version.
   *
   * **A superseded file is RETAINED.** Replacing one archives it into the history and points
   * the document at the new bytes; nothing destroys what it superseded. This is how a reader
   * opens it, and without it the history is a list of filenames nobody can reach.
   *
   * The signal is for the PREVIEW, which fetches on a modal the reader can close again.
   */
  downloadVersion: (id: string, versionId: string, signal?: AbortSignal) =>
    apiBlob(`/api/repository/${id}/versions/${versionId}/file`, { signal }),

  /**
   * Pins or unpins this item for the calling reader; resolves with whether it is now pinned.
   *
   * A pin is PERSONAL — it changes nothing another reader sees — which is why it carries
   * `repository.read` rather than a write key.
   */
  togglePin: (id: string) => api<boolean>(`/api/repository/${id}/pin`, { method: 'POST' }),

  /*
    There is no `pins`. The pinned view is `browse({ pinnedOnly: true })`, so it arrives with the
    search, the sort, the pager and the facets every other listing has — and whether ONE item is
    pinned is `isPinned` on the row, rather than a list fetched to answer a boolean.
  */

  /**
   * The decrypted file. The server reassembles both parts and verifies the hash first, and
   * names the download after the REFERENCE NUMBER rather than whatever the uploader called it.
   *
   * The signal is for the PREVIEW, which fetches on a tab the reader can leave again — a
   * controlled document is up to 50 MB, so a superseded request has to be droppable. A
   * download proper passes none: it was asked for explicitly and runs to completion.
   */
  download: (id: string, signal?: AbortSignal) =>
    apiBlob(`/api/repository/${id}/file`, { signal }),

  createFolder: (request: CreateRepositoryFolderRequest) =>
    api<RepositoryItemListItem>('/api/repository/folders', { method: 'POST', body: request }),

  updateFolder: (id: string, request: UpdateRepositoryFolderRequest) =>
    api<RepositoryItemListItem>(`/api/repository/folders/${id}`, { method: 'PUT', body: request }),

  /** What the repository holds, and what the volume behind it has room for. */
  storage: (signal?: AbortSignal) =>
    api<RepositoryStorage>('/api/repository/storage', { signal }),

  /** Soft delete. The stored bytes are kept; only erase removes those. */
  deactivate: (id: string) => api<void>(`/api/repository/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/repository/${id}/restore`, { method: 'POST' }),

  /** Soft-deletes every item at once; resolves with how many were deleted. */
  clear: () => api<number>("/api/repository/clear", { method: "POST" }),

  /* ── SHARING ──────────────────────────────────────────────────────────────────────── */

  /**
   * Who has access to an item, at what level, and whether this caller may change any of it.
   *
   * A READ under `repository.read` — knowing who to ask for access is not privileged. What IS
   * privileged is deciding it, and `canManage` on the result answers that.
   */
  shares: (id: string, signal?: AbortSignal) =>
    api<RepositoryShareSummary>(`/api/repository/${id}/shares`, { signal }),

  /**
   * Save the whole people-table at once — the dialog's Save sharing.
   *
   * REPLACES the list: whoever is missing is revoked in the same transaction that grants the
   * rest. Revoked rows are flagged, never removed.
   */
  saveShares: (id: string, request: SaveRepositorySharesRequest) =>
    api<void>(`/api/repository/${id}/shares`, { method: "PUT", body: request }),

  /** Set a FOLDER's general access — the "Anyone in the organization" half. */
  setGeneralAccess: (id: string, request: SetRepositoryGeneralAccessRequest) =>
    api<void>(`/api/repository/${id}/general-access`, { method: "PUT", body: request }),
};
